import { Tab, TablePagination, useMediaQuery } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid2'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import { useEffect, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { PROJECT_ROUTE, RECORDS_ROUTE } from '../../constants/routing.constants'
import { RecordListCard } from '../../pages/InternalPage/RecordsPage/components/RecordListCard'
import { useAssistantAdministrationStore } from '../../store/storeAssistantAdministration'
import { useWrapperStore } from '../../store/storeWrapper'
import { theme } from '../../theme'
import { RecordUpload } from '../RecordUpload'
import { RequestInput } from '../RequestInput'
import { WrapperCenter } from '../WrapperCenter'
import FilmIcon from './../../assets/icons/Film.svg?react'

export const Records = ({ isWorkplaceArchived = false }) => {
    const { isLoadingFetchRecord, setRecord, fetchRecord, records } =
        useAssistantAdministrationStore((state) => state)
    const { projectDetail, projectListActive } = useWrapperStore((state) => state)

    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const [searchParams, setSearchParams] = useSearchParams()
    const { pathname } = useLocation()

    const searchParamIsArchived = searchParams.get('is_archived')
    const searchParamOffset = searchParams.get('offset')
    const searchParamSorting = searchParams.get('desc')
    const searchParamOrdering = searchParams.get('order_by')
    const searchParamWorkplaceId = searchParams.get('workplaceId')

    const isArchived = searchParamIsArchived === 'true'

    const [value, setValue] = useState(Number(isArchived))
    const [page, setPage] = useState(0)
    const [count, setCount] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(24)

    const handleChangePage = async (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(+event.target.value)
        setPage(0)
    }

    useEffect(() => {
        searchParams.set('limit', rowsPerPage)
        searchParams.set('offset', page === 0 ? 0 : page * rowsPerPage)

        setSearchParams(searchParams)
    }, [page, rowsPerPage])

    useEffect(() => {
        if (records?.total_count) {
            setCount(records.total_count)
        }
    }, [records])

    useEffect(() => {
        if (searchParamOffset == '0') {
            setPage(0)
        }

        setValue(searchParamIsArchived === 'true' ? 1 : 0)
    }, [searchParamOffset, searchParamIsArchived])

    const handleChange = async (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue)
        setRecord(undefined)
        searchParams.set('is_archived', newValue === 1 ? 'true' : 'false')
        searchParams.set('offset', 0)
        setSearchParams(searchParams)
    }

    useEffect(() => {
        fetchRecord({
            desc: searchParamSorting,
            order_by: searchParamOrdering,
            is_archived: isArchived,
            workplace_id: searchParamWorkplaceId,
            limit: rowsPerPage,
            offset: page === 0 ? 0 : page * rowsPerPage,
        })
    }, [
        rowsPerPage,
        page,
        isArchived,
        searchParamSorting,
        searchParamOrdering,
        searchParamWorkplaceId,
    ])

    return (
        <Stack
            sx={{
                padding: searchParamWorkplaceId ? '16px 16px 0' : 2,
                position: 'relative',
                background: '#fff',
                height: '100%',
            }}>
            {pathname === RECORDS_ROUTE ? (
                <Grid
                    display="flex"
                    sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}>
                    <Tabs
                        value={value}
                        onChange={handleChange}
                        sx={{
                            borderBottom: 'none',
                            p: 0,
                        }}>
                        {!projectDetail?.is_archived && (
                            <Tab
                                variant="secondary"
                                disabled={isLoadingFetchRecord}
                                label={
                                    <Stack spacing={1} direction="row">
                                        <Typography>Все</Typography>
                                        {value === 0 && (
                                            <Typography
                                                sx={{
                                                    background: '#fff',
                                                    borderRadius: '50px',
                                                    height: '22px',
                                                    width: '20px',
                                                    fontSize: '12px',
                                                }}>
                                                {records?.objects?.length}
                                            </Typography>
                                        )}
                                    </Stack>
                                }
                                id="0"
                            />
                        )}

                        <Tab
                            variant="secondary"
                            disabled={
                                isWorkplaceArchived ||
                                isLoadingFetchRecord ||
                                projectDetail?.is_archived
                            }
                            label={
                                <Stack spacing={1} direction="row">
                                    <Typography>Архивированные</Typography>
                                    {value === 1 && (
                                        <Typography
                                            sx={{
                                                background: '#fff',
                                                borderRadius: '50px',
                                                height: '22px',
                                                width: '20px',
                                                fontSize: '12px',
                                            }}>
                                            {records?.objects?.length}
                                        </Typography>
                                    )}
                                </Stack>
                            }
                            id="1"
                        />
                    </Tabs>
                </Grid>
            ) : null}

            {isLoadingFetchRecord && (
                <WrapperCenter>
                    <CircularProgress />
                </WrapperCenter>
            )}

            {!isLoadingFetchRecord && records?.objects?.length === 0 && (
                <>
                    <Stack
                        size={12}
                        textAlign="center"
                        display="flex"
                        sx={{
                            height: `100%`,
                            p: 0,
                            alignItems: 'center',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignContent: 'center',
                        }}>
                        <Paper
                            sx={{
                                background: '#F9FAFB',
                                padding: searchParamWorkplaceId ? '16px 16px 0' : 2,
                                borderRadius: '10px',
                                mb: 2,
                            }}>
                            <FilmIcon />
                        </Paper>

                        {projectDetail?.is_archived || isArchived ? (
                            <>
                                <Typography variant="h5">Нет архивных записей</Typography>
                            </>
                        ) : (
                            <Stack>
                                <Typography variant="h5">У вас нет загруженных записей</Typography>
                                <Typography
                                    variant="caption2"
                                    sx={{ color: '#5A5F72', maxWidth: '252px', mt: 2, mb: 2 }}>
                                    Здесь будут отображаться все записи в видео и аудиоформатах
                                </Typography>
                                <RecordUpload />
                            </Stack>
                        )}
                    </Stack>
                </>
            )}

            {!isLoadingFetchRecord && records?.objects?.length > 0 && (
                <Stack
                    sx={{
                        p: 0,
                        overflowY: 'auto',
                        height: '100%',
                        alignContent: 'flex-start',
                    }}>
                    <Stack
                        sx={{
                            p: 0,
                            overflow: 'auto',
                            height: '100%',
                        }}>
                        <Grid
                            container
                            spacing={2}
                            sx={{
                                display: 'flex',
                                width: '100%',
                                height: '100%',
                                overflow: 'auto',
                                flexGrow: 1,
                                flexBasis: 0,
                            }}>
                            <RecordListCard records={records} />
                        </Grid>
                        <TablePagination
                            labelDisplayedRows={({ from, to, count }) =>
                                `${from}–${to} из ${count !== -1 ? count : `больше, чем ${to}`}`
                            }
                            labelRowsPerPage={isMobile ? 'Всего' : 'Количество записей'}
                            rowsPerPageOptions={[24, 64, 128]}
                            component="div"
                            count={count}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{
                                minHeight: 'fit-content',

                                '& .MuiTablePagination-toolbar': {
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    justifyContent: 'flex-end',
                                },
                                '& .MuiTablePagination-spacer': {
                                    flex: 'none',
                                },
                                '& .MuiTablePagination-actions': {
                                    marginLeft: isMobile ? 'auto' : 0,
                                },
                                '& .MuiTablePagination-select': {
                                    [theme.breakpoints.down('sm')]: {
                                        display: 'none',
                                    },
                                },
                                '& .MuiTablePagination-selectLabel': {
                                    [theme.breakpoints.down('sm')]: {
                                        display: 'none',
                                    },
                                },
                            }}
                        />
                    </Stack>
                </Stack>
            )}
        </Stack>
    )
}
